import type { QuizProps } from '@/components/quiz/quiz.types';
import { useRef, useState } from 'react';
import { postSuccessPhpWithParams } from '@/api/requests';
import Questionnaire from '@/components/questionnaire';
import QuizSuccess from '@/components/quiz/components/quiz-success';
import QuizHead from '@/components/quiz/components/quiz-head';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { SliderSlice } from '@/redux/slider';
import { ID } from '@/utils/enums';
import { cn } from '@/utils/helper';
import Track from '@/utils/track';
import v1 from '@/components/quiz/quiz-v1.module.css';
import v2 from '@/components/quiz/quiz-v2.module.css';

export default function Quiz(props: QuizProps) {
  const dialog = useAppSelector(ConfigSlice.selectors.additionalQuestions);
  const statics = useAppSelector(ConfigSlice.selectors.statics);
  const prioCalculatorVersion = useAppSelector(ConfigSlice.selectors.prioCalculatorVersion);
  const useDynamicLandingPage = useAppSelector(ConfigSlice.selectors.useDynamicLandingPage);
  const percentDynamicLandingPageOnPrio = useAppSelector(ConfigSlice.selectors.percentDynamicLandingPageOnPrio);
  const via = useAppSelector(ConfigSlice.selectors.via);
  const leadId = useAppSelector(GeneralSlice.selectors.leadId);
  const testVariation = useAppSelector(GeneralSlice.selectors.testVariation);
  const forcedPriorityFour = useAppSelector(GeneralSlice.selectors.forcedPriorityFour);
  const leadPlatform = useAppSelector(GeneralSlice.selectors.leadPlatform);
  const answersDirect = useAppSelector(SliderSlice.selectors.answersDirect);
  const [isAnswered, setIsAnswered] = useState<boolean>(false);
  const iustaCaseId = useRef<string>('');

  if (!dialog) return null;

  const onComplete = async (questionnaire: Record<string, string>): Promise<void> => {
    const modalMain = document.querySelector('.modal__main');
    const modalContent = document.querySelector('.modal__content');

    if (modalMain && modalContent) {
      // TODO: to be careful while modal replace.
      modalMain.setAttribute('style', 'overflow: visible;');
      modalContent.setAttribute('style', 'overflow: visible;');
    }

    if (!dialog.postAfterEach) {
      try {
        await postSuccessPhpWithParams(questionnaire, {
          leadId,
          testVariation,
          forcedPriorityFour,
          leadPlatform,
          iustaCaseId: iustaCaseId.current,
        });
        setIsAnswered(true);
      } catch {
        setIsAnswered(true);
      }
    }

    Track.Experiments.AdditionalQuestionsSubmit();
  };

  const onNext = async (
    current: string,
    previous?: string,
    questionnaire?: Record<string, string>,
    isLast?: boolean,
  ): Promise<void> => {
    if (current) {
      const event = new Event(`weiter-button-${current}`);
      document.dispatchEvent(event);

      if (questionnaire && Object.keys(questionnaire).length) {
        if (dialog.postAfterEach) {
          // TODO: Alert compare with current production, some data is not sent. Like prios and so on
          const payload = {
            additional_answers: questionnaire,
            post_data: {
              ...answersDirect,
              agb_version: statics ? statics.agb_version : '',
              dynamicLandingPage: Number(useDynamicLandingPage),
              dynamicLandingPagePercent:
                useDynamicLandingPage && percentDynamicLandingPageOnPrio
                  ? percentDynamicLandingPageOnPrio
                  : [0, 0, 0, 0],
              prioCalculatorV2: prioCalculatorVersion.v2,
              prioCalculatorV3: prioCalculatorVersion.v3,
              selectedCalculatorVersion: prioCalculatorVersion.selected,
              via: via,
            },
          };
          const maxAttempts = 3;
          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
              const response = await postSuccessPhpWithParams(payload, {
                leadId,
                testVariation,
                forcedPriorityFour,
                leadPlatform,
                iustaCaseId: iustaCaseId.current,
              });
              if (response?.iustaCaseId) iustaCaseId.current = String(response.iustaCaseId);
              break;
            } catch {
              if (attempt < maxAttempts) {
                await new Promise((resolve) => setTimeout(resolve, attempt * 3000));
              }
            }
          }
          if (isLast) {
            setIsAnswered(true);
          }
        }

        Track.Experiments.AdditionalQuestionsSubmitKey(previous);
      }

      if (previous === undefined && questionnaire === undefined && isLast) {
        setIsAnswered(true);
      }
    }
  };

  return (
    <div id={ID.QUIZ} className={cn(v1.Quiz, v2.Quiz)}>
      {isAnswered ? (
        <QuizSuccess onClick={props.onCompleteQuiz} />
      ) : (
        <div className={cn(v1.Content, v2.Content)}>
          <QuizHead />
          <Questionnaire questions={dialog.main} onNext={onNext} onComplete={onComplete} />
        </div>
      )}
    </div>
  );
}
